/** Finite, foreground microphone capture. Muting disposes every audio resource. */
export interface CaptureAdapters {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  context(): AudioContext;
  node(context: AudioContext): AudioWorkletNode;
}
export const captureAdapters: CaptureAdapters = {
  getUserMedia: constraints => navigator.mediaDevices.getUserMedia(constraints),
  context: () => new AudioContext({ sampleRate: 24000 }),
  node: context => new AudioWorkletNode(context, 'command-capture'),
};

export function pcmWav(samples: Float32Array, rate = 24000): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const text = (offset: number, value: string) => [...value].forEach((c, i) => view.setUint8(offset + i, c.charCodeAt(0)));
  text(0, 'RIFF'); view.setUint32(4, buffer.byteLength - 8, true); text(8, 'WAVE');
  text(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
  view.setUint16(22, 1, true); view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  text(36, 'data'); view.setUint32(40, samples.length * 2, true);
  samples.forEach((value, i) => {
    const sample = Math.max(-1, Math.min(1, value));
    view.setInt16(44 + i * 2, sample * (sample < 0 ? 32768 : 32767), true);
  });
  return new Blob([buffer], { type: 'audio/wav' });
}

export class CommandCapture {
  private generation = 0;
  private stream?: MediaStream;
  private context?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private node?: AudioWorkletNode;
  private gain?: GainNode;
  private chunks: Float32Array[] = [];
  private length = 0;
  private timer?: ReturnType<typeof setTimeout>;
  constructor(private adapters: CaptureAdapters = captureAdapters) {}

  async start(onLimit: () => void, onLevel?: (level: number) => void): Promise<boolean> {
    this.cancel();
    const generation = this.generation;
    let pendingContext: AudioContext | undefined;
    try {
      // Create/resume synchronously from the button gesture for mobile browsers.
      pendingContext = this.adapters.context();
      this.context = pendingContext;
      const resumed = pendingContext.resume();
      void resumed.catch(() => {}); // A canceled permission prompt may close this context first.
      const stream = await this.adapters.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true }, video: false });
      if (generation !== this.generation) { stream.getTracks().forEach(t => t.stop()); return false; }
      this.stream = stream;
      await resumed;
      await pendingContext.audioWorklet.addModule('/capture-worklet.js');
      if (generation !== this.generation) return false;
      if (pendingContext.sampleRate !== 24000) throw new Error('This browser cannot capture the required audio format.');
      this.source = pendingContext.createMediaStreamSource(stream);
      this.node = this.adapters.node(pendingContext);
      this.gain = pendingContext.createGain();
      this.gain.gain.value = 0; // Never play the microphone back to the user.
      this.node.port.onmessage = event => {
        if (generation !== this.generation || !(event.data instanceof Float32Array)) return;
        onLevel?.(rms(event.data));
        const chunk = event.data.slice(0, Math.max(0, 720000 - this.length));
        this.chunks.push(chunk); this.length += chunk.length;
        if (this.length >= 720000) onLimit();
      };
      this.source.connect(this.node); this.node.connect(this.gain); this.gain.connect(pendingContext.destination);
      this.timer = setTimeout(onLimit, 30000);
      return true;
    } catch (error) {
      if (generation !== this.generation) return false;
      this.cancel();
      throw error;
    }
  }

  finish(): Blob {
    const samples = new Float32Array(this.length);
    let offset = 0;
    for (const chunk of this.chunks) { samples.set(chunk, offset); offset += chunk.length; }
    this.cancel();
    if (samples.length < 2400) throw new Error('Record a command before sending.');
    return pcmWav(samples);
  }

  cancel(): void {
    this.generation++;
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.stream?.getTracks().forEach(track => track.stop()); this.stream = undefined;
    if (this.node) { this.node.port.onmessage = null; this.node.disconnect(); }
    this.source?.disconnect(); this.gain?.disconnect();
    this.node = undefined; this.source = undefined; this.gain = undefined;
    void this.context?.close().catch(() => {}); this.context = undefined;
    this.chunks = []; this.length = 0;
  }
}

function rms(samples: Float32Array): number {
  if (!samples.length) return 0;
  let sum = 0;
  for (const sample of samples) sum += sample * sample;
  return Math.min(1, Math.sqrt(sum / samples.length) * 4);
}
